import { createClientErrorId } from "./clientErrorIds";
import type { ClientErrorReporter } from "./clientErrorTypes";

const HTTP_NOT_FOUND = 404;
const HTTP_SERVER_ERROR_MIN = 500;
const HTTP_SERVICE_UNAVAILABLE = 503;
const API_ERROR_SERVICE_UPDATING = "ApiServiceUpdating";
const defaultReportableApiErrorCodes = new Set(["ApiEndpointNotFound", "ApiServiceUnavailable", "ApiUnexpectedResponse"]);
const htmlResponsePattern = /^\s*(?:<!doctype\s+html|<html|<head|<body)\b/i;
const defaultMaxPlainErrorMessageLength = 500;

export type ApiRequestContext = {
  method: string;
  url: string;
};

export type ApiErrorReaderOptions = {
  maxPlainErrorMessageLength?: number;
  readKnownError?: (res: Response) => string | undefined;
  reportableApiErrorCodes?: ReadonlySet<string>;
  reportError?: ClientErrorReporter;
  translatableApiErrorKeys: ReadonlySet<string>;
};

const responseRequestContexts = new WeakMap<Response, ApiRequestContext>();

export class ApiError extends Error {
  code?: string;
  status: number;
  details?: unknown;
  requestMethod?: string;
  requestUrl?: string;

  constructor(message: string, status: number, code?: string, details?: unknown, requestContext?: ApiRequestContext) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestMethod = requestContext?.method;
    this.requestUrl = requestContext?.url;
  }
}

export const recordApiResponseContext = (res: Response, context: ApiRequestContext) => {
  responseRequestContexts.set(res, context);
};

const readApiErrorDetail = (details: unknown, key: string) => {
  if (!details || typeof details !== "object") return undefined;
  const value = (details as Record<string, unknown>)[key];
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : undefined;
};

const createApiErrorReporter = (options: ApiErrorReaderOptions) => (error: ApiError, fallback: string) => {
  if (error.status === HTTP_SERVICE_UNAVAILABLE && error.code === API_ERROR_SERVICE_UPDATING) return;
  const reportableCodes = options.reportableApiErrorCodes ?? defaultReportableApiErrorCodes;
  if (!options.reportError || (error.status < HTTP_SERVER_ERROR_MIN && (!error.code || !reportableCodes.has(error.code)))) return;

  const method = error.requestMethod ?? "UNKNOWN";
  const endpoint = error.requestUrl ?? "unknown";
  const code = error.code ?? readApiErrorDetail(error.details, "error") ?? null;
  const responseMessage = readApiErrorDetail(error.details, "message") ?? readApiErrorDetail(error.details, "title") ?? null;

  options.reportError({
    errorId: createClientErrorId("ERR"),
    message: `${method} ${endpoint} failed (${error.status}${code ? ` ${code}` : ""})`,
    source: "api.request",
    sourceKind: "ApiError",
    url: typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}${window.location.hash}` : null,
    context: {
      method,
      endpoint,
      status: String(error.status),
      code,
      fallback,
      apiMessage: error.message,
      responseMessage
    }
  });
};

export const createApiErrorReader = (options: ApiErrorReaderOptions) => {
  const reportApiErrorIfNeeded = createApiErrorReporter(options);
  const maxPlainErrorMessageLength = options.maxPlainErrorMessageLength ?? defaultMaxPlainErrorMessageLength;

  const completeApiError = (res: Response, fallback: string, message: string, code?: string, details?: unknown) => {
    const error = new ApiError(message, res.status, code, details, responseRequestContexts.get(res));
    reportApiErrorIfNeeded(error, fallback);
    return error;
  };

  const readApiError = async (res: Response, fallback: string) => {
    const knownError = options.readKnownError?.(res);
    if (knownError) {
      return completeApiError(res, fallback, knownError, knownError);
    }

    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const err = await res.json().catch(() => ({}));
      if (typeof err?.error === "string" && options.translatableApiErrorKeys.has(err.error)) {
        return completeApiError(res, fallback, err.error, err.error, err);
      }
      return completeApiError(res, fallback, err?.message || err?.title || fallback, err?.error, err);
    }

    const text = await res.text().catch(() => "");
    if (!text) return completeApiError(res, fallback, fallback);
    if (contentType.includes("text/html") || htmlResponsePattern.test(text)) {
      if (res.status === HTTP_NOT_FOUND) return completeApiError(res, fallback, "ApiEndpointNotFound", "ApiEndpointNotFound");
      if (res.status >= HTTP_SERVER_ERROR_MIN) return completeApiError(res, fallback, "ApiServiceUnavailable", "ApiServiceUnavailable");
      return completeApiError(res, fallback, "ApiUnexpectedResponse", "ApiUnexpectedResponse");
    }

    const trimmed = text.trim();
    const firstLine = trimmed.split(/\r?\n/, 1)[0]?.trim() || "";
    const exceptionKeyMatch = firstLine.match(/^(?:System\.)?(?:Exception|InvalidOperationException):\s*([A-Za-z0-9_]+)$/);
    if (exceptionKeyMatch && options.translatableApiErrorKeys.has(exceptionKeyMatch[1])) {
      return completeApiError(res, fallback, exceptionKeyMatch[1], exceptionKeyMatch[1]);
    }

    return completeApiError(res, fallback, trimmed.length > maxPlainErrorMessageLength ? fallback : trimmed);
  };

  const readApiErrorMessage = async (res: Response, fallback: string) => {
    return (await readApiError(res, fallback)).message;
  };

  return {
    readApiError,
    readApiErrorMessage,
    /** Compatible with legacy string-reader callers: rejection preserves the full ApiError. */
    throwApiError: async (res: Response, fallback: string): Promise<never> => {
      throw await readApiError(res, fallback);
    },
  };
};
